import React, { useState, useEffect } from "react";
import { useParams, withRouter } from "react-router";
import { useSelector } from "react-redux";
import { RiLoaderLine, RiAddLine } from "react-icons/ri";
import moment from "moment";
import {
  getAiGlossaryById,
  updateAiGlossaryById,
  uploadAiGlossaryCoverImage,
  getAllAiGlossaryCategories,
} from "@api/seo/aiGlossary";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Spin,
  Modal,
} from "antd";
import { SuccessNotification, ErrorNotification } from "../..";
import ReactMarkdown from "react-markdown";

const initialState = {
  term: "",
  coverImage: "",
  description: "",
  content: "",
  category: "",
  publishedDate: "",
  images: [],
};

const AiGlossaryUpdateForm = ({ history }) => {
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);

  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    fetchAiGlossary();
  }, []);

  // Get aiGlossary by id
  const fetchAiGlossary = async () => {
    setLoading(true);
    try {
      const { data } = await getAiGlossaryById(id);

      // Fetch categories
      const cats = await getAllAiGlossaryCategories();
      setCategories(cats.data);

      // Set category
      data.category = data?.category?._id;

      // Convert date to moment object
      if (data.publishedDate) {
        data.publishedDate = moment(
          data.publishedDate?.split("T")[0],
          "YYYY/MM/DD"
        );
      }

      setValues({
        ...data,
        content: data.content,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("GET_BLOG_BY_ID_ERROR", error);
    }
  };

  // Handle form changes
  const handleFormChange = (e) => {
    e.preventDefault();
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle form submit
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Set update loading to true
    setUpdateLoading(true);

    // Validate the form
    if (
      !values.term ||
      !values.description ||
      !values.category ||
      !values.publishedDate ||
      !values.content
    ) {
      setUpdateLoading(false);
      return ErrorNotification("Please fill all the required fields");
    }

    updateAiGlossaryById(id, values)
      .then(() => {
        SuccessNotification(`AI Glossary updated successfully`);

        // Set update loading to false
        setUpdateLoading(false);

        history.goBack();
      })
      .catch((error) => {
        setUpdateLoading(false);
        ErrorNotification("Error updating ai-app");
        console.log("UPDATE_BLOG_ERROR", error);
      });
  };

  // Before image upload
  const beforeCoverUpload = (file) => {
    // Image can be jpeg, png, or gif
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPEG/PNG files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must smaller than 3MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // Handle cover image upload
  const handleUploadCoverImage = async (options) => {
    const { onError, file } = options;
    const fmData = new FormData();

    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    fmData.append("image", file);

    // Make request to upload image
    try {
      setLoading(true);
      const res = await uploadAiGlossaryCoverImage(fmData, config);
      setValues({
        ...values,
        coverImage: res.data.url,
      });
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const uploadButton = (
    <div>
      {loading ? (
        <RiLoaderLine
          className="remix-icon-loading remix-icon-spin remix-icon"
          size={24}
        />
      ) : (
        <RiAddLine size={24} />
      )}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Row>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(80vh - 50px)",
            width: "100%",
          }}
        >
          <Spin />
        </div>
      ) : (
        <Col
          span={24}
          style={{
            overflowY: "auto",
            height: "calc(100vh - 100px)",
          }}
        >
          <Form
            labelCol={{
              span: 4,
            }}
            wrapperCol={{
              span: 18,
            }}
          >
            {/* AI Glossary Term */}
            <Form.Item label="Term">
              <Input
                name="term"
                placeholder="Enter term"
                onChange={handleFormChange}
                value={values?.term}
                rules={[{ required: true }]}
              />
            </Form.Item>

            {/* AI Glossary cover image */}
            <Form.Item label="Cover Image">
              <Upload
                name="coverImage"
                listType="picture-card"
                customRequest={handleUploadCoverImage}
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeCoverUpload}
                multiple={false}
                accept="image/*,image/gif"
              >
                {values?.coverImage ? (
                  <img
                    src={values?.coverImage}
                    alt="avatar"
                    style={{ width: "100%" }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>

            {/* SEO meta description */}
            <Form.Item label="Meta Description">
              <Input
                name="description"
                placeholder="Enter meta description"
                onChange={handleFormChange}
                value={values?.description}
                rules={[{ required: true }]}
              />
            </Form.Item>

            {/* Select drop down with category */}
            <Form.Item label="Category">
              <Select
                name="category"
                placeholder="Select a category"
                onChange={(value) => setValues({ ...values, category: value })}
                value={values?.category}
                rules={[{ required: true }]}
              >
                {categories.map((category) => (
                  <Select.Option key={category._id} value={category._id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Publish date */}
            {!loading && (
              <Form.Item label="Published Date">
                <DatePicker
                  className="hp-mb-16 hp-mr-16"
                  onChange={(date, dateString) =>
                    setValues({ ...values, publishedDate: dateString })
                  }
                  defaultValue={values?.publishedDate}
                />
              </Form.Item>
            )}

            {/* AI Glossary Content */}
            {!loading && (
              <Form.Item label="Content">
                <Col
                  style={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div style={{ marginBottom: "16px" }}>
                    <Button
                      onClick={() => setPreviewVisible(true)}
                      style={{ marginRight: "16px" }}
                    >
                      Preview
                    </Button>
                  </div>
                  <Input.TextArea
                    name="content"
                    placeholder="Enter content in markdown format"
                    onChange={handleFormChange}
                    value={values?.content}
                    rules={[{ required: true }]}
                    rows={16} // Increase the number of rows to make the input field bigger
                  />
                </Col>
              </Form.Item>
            )}

            {/* Submit button */}
            <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
              <Button
                type="primary"
                onClick={handleFormSubmit}
                loading={updateLoading}
              >
                Update
              </Button>
            </Form.Item>
          </Form>
        </Col>
      )}

      <Modal
        title="Preview"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <ReactMarkdown>{values?.content}</ReactMarkdown>
      </Modal>
    </Row>
  );
};

export default withRouter(AiGlossaryUpdateForm);
